import React, { useRef } from 'react';
import { useContent } from '../../context/ContentContext';
import { resolveHeroSlides } from '../../data/homeHeroSlides';
import { promptImageUrl, readImageFile } from '../../utils/imageUpload';

export default function EditorPanelHeroSlidesDetail() {
  const { getContent, setContentValue } = useContent();
  const slides = resolveHeroSlides(getContent);
  const fileInputRef = useRef(null);
  const replaceIndexRef = useRef(null);

  const saveSlides = (nextSlides) => {
    setContentValue('home.hero.slides', nextSlides);
  };

  const updateSlide = (index, src) => {
    saveSlides(slides.map((slide, idx) => (idx === index ? src : slide)));
  };

  const removeSlide = (index) => {
    if (slides.length <= 1) {
      window.alert('You need at least one hero slide.');
      return;
    }
    if (!window.confirm('Remove this slide?')) return;
    saveSlides(slides.filter((_, idx) => idx !== index));
  };

  const addSlide = (src) => {
    saveSlides([...slides, src]);
  };

  const applyFile = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await readImageFile(file);
      const index = replaceIndexRef.current;
      if (index === null) return;
      if (index >= slides.length) addSlide(dataUrl);
      else updateSlide(index, dataUrl);
    } catch (error) {
      window.alert(error.message);
    } finally {
      replaceIndexRef.current = null;
    }
  };

  const openUpload = (index) => {
    replaceIndexRef.current = index;
    fileInputRef.current?.click();
  };

  return (
    <div className="editor-panel__detail" onClick={(e) => e.stopPropagation()}>
      <p className="editor-panel__detail-lead">Preview each slide, paste a URL, or upload a replacement.</p>

      <ul className="editor-panel__detail-list">
        {slides.map((slide, index) => (
          <li key={`slide-${index}`} className="editor-panel__detail-card">
            <div className="editor-panel__detail-card-head">
              <div className="editor-panel__detail-thumb-wrap editor-panel__detail-thumb-wrap--wide">
                {slide ? (
                  <img src={slide} alt="" className="editor-panel__detail-thumb" />
                ) : (
                  <span className="editor-panel__detail-thumb-placeholder" aria-hidden="true" />
                )}
              </div>
              <span className="editor-panel__detail-card-title">Slide {index + 1}</span>
            </div>

            <label className="editor-panel__detail-label" htmlFor={`hero-slide-${index}-url`}>
              Image URL
            </label>
            <input
              id={`hero-slide-${index}-url`}
              type="url"
              className="editor-panel__detail-input"
              value={slide}
              placeholder="https://..."
              onChange={(e) => updateSlide(index, e.target.value)}
            />

            <div className="editor-panel__detail-btn-row">
              <button type="button" className="editor-panel__detail-btn" onClick={() => openUpload(index)}>
                Upload
              </button>
              <button
                type="button"
                className="editor-panel__detail-btn"
                onClick={() => {
                  const url = promptImageUrl();
                  if (url) updateSlide(index, url);
                }}
              >
                Paste URL
              </button>
              <button
                type="button"
                className="editor-panel__detail-btn editor-panel__detail-btn--danger"
                onClick={() => removeSlide(index)}
              >
                Remove
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="editor-panel__detail-btn-row editor-panel__detail-btn-row--footer">
        <button type="button" className="editor-panel__detail-btn editor-panel__detail-btn--primary" onClick={() => openUpload(slides.length)}>
          + Add slide (upload)
        </button>
        <button
          type="button"
          className="editor-panel__detail-btn"
          onClick={() => {
            const url = promptImageUrl();
            if (url) addSlide(url);
          }}
        >
          + Add from URL
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="editable-image-input"
        onChange={(e) => {
          applyFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}
